import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

import { ProductLotStockInForm } from './new'
import type { CreateInNoteRequest } from '@/lib/api/product-lot-api'
import type { ProductLotOption } from '@/components/product-lots/ProductLotItemsField'
import { productNoteItemToOption } from '@/components/product-lots/product-lot-option'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useProductLotInNoteQuery } from '@/hooks/use-product-lot-query'
import { useUpdateProductLotInNote } from '@/hooks/use-product-lot-mutation'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/stock-in-notes/$id_/edit',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data, isLoading } = useProductLotInNoteQuery(id)
    const note = data?.data
    const update = useUpdateProductLotInNote()

    // Lô lấy hương nguồn đã chọn (seed để picker hiện đúng dù ngoài top-search).
    const seedSourceOptions = useMemo(
        () =>
            (note?.items ?? []).flatMap((it) =>
                (it.sources ?? []).map((s) => ({
                    value: s.source_scenting_lot_id,
                    label:
                        s.source_scenting_lot_code ?? s.source_scenting_lot_id,
                })),
            ),
        [note],
    )
    // Lô vật tư đã chọn ở bảng con (seed để picker hiện đúng dù ngoài top-search).
    const seedMaterialOptions = useMemo(
        () =>
            (note?.items ?? [])
                .flatMap((it) => it.materials ?? [])
                .map((mat) => ({
                    value: mat.packaging_lot_id,
                    label:
                        mat.packaging_lot_code ??
                        mat.material_name ??
                        mat.packaging_lot_id,
                })),
        [note],
    )

    // Lô của item hiện có: lô mới do PNK tạo đang 'pending' nên query lô active
    // không trả về → seed để ô "Chọn lô" hiển thị đúng nhãn.
    const seedLotOptions = useMemo<ProductLotOption[]>(
        () => (note?.items ?? []).map(productNoteItemToOption),
        [note],
    )

    const defaultValues = useMemo(() => {
        if (!note) return null
        return {
            reason: note.reason,
            scenting_lot_stock_out_note_id: '',
            packaging_lot_stock_out_note_id:
                note.packaging_lot_stock_out_note_id ?? '',
            notes: note.notes ?? '',
            items: (note.items ?? []).map((it) => ({
                kind: 'existing' as const,
                product_lot_id: it.product_lot_id,
                quantity: it.quantity,
                sources: (it.sources ?? []).map((s) => ({
                    source_lot_id: s.source_scenting_lot_id,
                    source_quantity: s.source_quantity,
                })),
                materials: (it.materials ?? []).map((mat) => ({
                    packaging_lot_id: mat.packaging_lot_id,
                    quantity: mat.quantity,
                })),
            })),
        }
    }, [note])

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note || !defaultValues)
        return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable =
        note.status === 'pending' && isCreator && !note.stocktake_session_id

    if (!editable) {
        return (
            <div className="space-y-4 p-6">
                <Button asChild variant="ghost" className="-ml-2">
                    <Link
                        to="/sales-warehouse/stock-in-notes/$id"
                        params={{ id }}
                    >
                        <ArrowLeft className="size-4 mr-1" />
                        {m.sl_lot_back()}
                    </Link>
                </Button>
                <p className="text-muted-foreground">
                    {m.pl_pnk_edit_not_allowed()}
                </p>
            </div>
        )
    }

    const backToDetail = () =>
        navigate({
            to: '/sales-warehouse/stock-in-notes/$id',
            params: { id },
            replace: true,
        })

    const handleSubmit = async (req: CreateInNoteRequest) => {
        await update.mutateAsync({ id, req })
        void backToDetail()
    }

    return (
        <ProductLotStockInForm
            mode="edit"
            title={m.pll_pnk_edit_title()}
            submitLabel={m.pl_pnk_edit_submit()}
            isPending={update.isPending}
            defaultValues={defaultValues}
            initialWithdrawalNoteId={
                note.scenting_lot_withdrawal_note_id ?? undefined
            }
            initialPackagingWithdrawalNoteId={
                note.packaging_lot_withdrawal_note_id ?? undefined
            }
            seedLotOptions={seedLotOptions}
            seedSourceOptions={seedSourceOptions}
            seedMaterialOptions={seedMaterialOptions}
            onSubmit={handleSubmit}
            onCancel={() => void backToDetail()}
        />
    )
}
